<template>
  <div class="tw-space-y-2 tw-p-2">
    <!-- Welcome Header -->
    <div class="tw-bg-gradient-to-r tw-from-primary-500 tw-to-primary-600 tw-rounded-lg tw-p-6 tw-text-white">
      <div v-if="!loading" class="tw-flex tw-items-center tw-justify-between">
        <div>
          <h1 class="tw-text-2xl tw-font-bold tw-mb-2">{{ selectedWelcome.message }}</h1>
          <p class="tw-text-primary-100 tw-text-sm">
            {{ selectedWelcome.subtitle }}
          </p>
        </div>
        <div class="tw-text-right">
          <div class="tw-text-3xl tw-font-bold tw-capitalize">{{ getCurrentDate() }}</div>
          <div class="tw-text-primary-100 tw-text-sm">{{ getCurrentTime() }}</div>
        </div>
      </div>
      <div v-else class="tw-animate-pulse">
        <div class="tw-h-8 tw-bg-white tw-bg-opacity-20 tw-rounded tw-w-64 tw-mb-2"></div>
        <div class="tw-h-4 tw-bg-white tw-bg-opacity-20 tw-rounded tw-w-80"></div>
      </div>
    </div>

    <!-- Main Statistics Grid -->
    <div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-2">
      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.total-companies')"
        :value="dashboardStats.companies"
        icon="solar:buildings-2-bold-duotone"
        icon-color="tw-text-purple-600"
        icon-bg-color="tw-bg-purple-100"
      />
      
      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.total-users')"
        :value="dashboardStats.users"
        icon="solar:users-group-rounded-bold-duotone"
        icon-color="tw-text-blue-600"
        icon-bg-color="tw-bg-blue-100"
      />
      
      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.active-events')"
        :value="dashboardStats.sessions"
        icon="solar:calendar-bold-duotone"
        icon-color="tw-text-orange-600"
        icon-bg-color="tw-bg-orange-100"
      />
      
      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.total-products')"
        :value="dashboardStats.products"
        icon="solar:box-bold-duotone"
        icon-color="tw-text-green-600"
        icon-bg-color="tw-bg-green-100"
      />

      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.total-services')"
        :value="dashboardStats.services"
        icon="solar:settings-bold-duotone"
        icon-color="tw-text-indigo-600"
        icon-bg-color="tw-bg-indigo-100"
      />

      <DashboardStatsCard
        :loading="loading"
        :title="$t('dashboard.admin.stats.active-sessions')"
        :value="dashboardStats.active_sessions"
        icon="solar:chat-round-call-bold-duotone"
        icon-color="tw-text-pink-600"
        icon-bg-color="tw-bg-pink-100"
      />
    </div>

    <!-- Online Users Section -->
    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2">
      <!-- Online Users Widget -->
      <div class="lg:tw-col-span-1">
        <OnlineUsersWidget />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import DashboardStatsCard from '@/components/dashboard/admin/DashboardStatsCard.vue'
import OnlineUsersWidget from '@/components/dashboard/admin/users/OnlineUsersWidget.vue'
import { admin } from '@/api/dashboard'
import { useRandomWelcome } from '@/composables/useRandomWelcome'

const { locale } = useI18n()

// Random welcome message
const { selectedWelcome } = useRandomWelcome()

// Loading state
const loading = ref(true)

// Dashboard data
const dashboardStats = ref({
  companies: '1,247',
  companiesGrowth: '+12%',
  users: '8,592',
  usersGrowth: '+8%',
  sessions: '5',
  eventsGrowth: '0%',
  products: '3,458',
  productsGrowth: '+15%',
  services: '892',
  servicesGrowth: '+6%',
  active_sessions: '124',
  sessionsGrowth: '+18%'
})

// Methods
const getCurrentDate = () => {
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    fr: 'fr-FR',
    es: 'es-ES',
    pt: 'pt-BR'
  }
  
  return new Date().toLocaleDateString(localeMap[locale.value] || 'en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  })
}

const getCurrentTime = () => {
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    fr: 'fr-FR',
    es: 'es-ES',
    pt: 'pt-BR'
  }
  
  return new Date().toLocaleTimeString(localeMap[locale.value] || 'en-US', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Simulate loading
onMounted(() => {
  admin.overview().then(response => {
    dashboardStats.value = response.data
    loading.value = false
  })
})
</script>

<style></style>